import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { FONT_SYSTEM } from 'components/v3/tokens';
import { brand } from '../shared/brand';
import { ORANGE, TIP_BG, TIP_ID, WATCH_MS, Z } from './constants';
import { placeTip } from './placement';
import { isActiveTipLayer, registerTipLayer } from './tipBus';
import { createTipController } from './tipController';
import { isHelpTip, isShortLabel, keysOf } from './tipTargets';
import { isHelpTipsEnabled, subscribeHelpTips } from './tipsSetting';
import { resolveRegistry } from './tooltips';
import { getTourState } from './tourStore';

// The one branded tooltip for the Forms area (Implementation List §3,
// design tooltips.js + help.css). Mount it once per Forms page (FormsHelp
// does); it watches the whole document, so any element with data-tip, a
// REGISTRY match or a native title gets the same tip. Two looks: a label
// ("Close Esc") and a help tip (bold title, explanation, 2px orange bar).

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ARROW = {
  top: 'bottom: -5px; margin-left: -4.5px; border-top-color: transparent; border-left-color: transparent;',
  bottom:
    'top: -5px; margin-left: -4.5px; border-bottom-color: transparent; border-right-color: transparent;',
  left: 'right: -5px; margin-top: -4.5px; border-bottom-color: transparent; border-left-color: transparent;',
  right:
    'left: -5px; margin-top: -4.5px; border-top-color: transparent; border-right-color: transparent;',
};

const Box = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  z-index: ${Z.tip};
  box-sizing: border-box;
  max-width: min(300px, calc(100vw - 16px));
  padding: 6px 10px;
  border-radius: 8px;
  background: ${({ $tone }) => TIP_BG[$tone]};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'light' ? TIP_BG.light : 'rgba(255, 255, 255, 0.14)'};
  box-shadow: ${({ $tone }) =>
    $tone === 'light'
      ? '0 10px 28px rgba(23, 37, 69, 0.28)'
      : '0 10px 28px rgba(0, 0, 0, 0.45)'};
  color: #fff;
  font-family: ${FONT_SYSTEM};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.1px;
  text-align: left;
  text-transform: none;
  pointer-events: none;
  animation: ${fadeIn} 120ms ease;

  ${({ $rich, $short }) =>
    $rich
      ? css`
          width: max-content;
          max-width: min(290px, calc(100vw - 16px));
          padding: 11px 13px 12px;

          &::before {
            content: '';
            position: absolute;
            left: 12px;
            right: 12px;
            top: -1px;
            height: 2px;
            border-radius: 0 0 2px 2px;
            background: ${ORANGE};
          }
        `
      : css`
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: ${$short ? 'nowrap' : 'normal'};
        `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Title = styled.div`
  margin-bottom: 3px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.1px;
`;

const Body = styled.div`
  ${({ $rich }) =>
    $rich &&
    css`
      font-weight: 500;
      line-height: 1.5;
      color: rgba(255, 255, 255, 0.84);
    `}
`;

const Keys = styled.div`
  display: flex;
  gap: 4px;
  margin-top: ${({ $rich }) => ($rich ? '8px' : '0')};
`;

const Kbd = styled.kbd`
  min-width: 18px;
  height: 18px;
  box-sizing: border-box;
  padding: 0 5px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  font: 700 10.5px/16px ${FONT_SYSTEM};
  text-align: center;
`;

const Arrow = styled.span`
  position: absolute;
  width: 9px;
  height: 9px;
  box-sizing: border-box;
  background: inherit;
  border: inherit;
  transform: rotate(45deg);
  ${({ $side }) => ARROW[$side] || ''}
`;

const samePos = (a, b) =>
  !!a &&
  !!b &&
  a.left === b.left &&
  a.top === b.top &&
  a.side === b.side &&
  a.arrow === b.arrow;

const TooltipLayer = () => {
  const theme = useTheme();
  const tone = theme && theme.name === 'dark' ? 'dark' : 'light';
  const registry = useMemo(() => resolveRegistry(brand().name), []);
  const [tip, setTip] = useState(null);
  const [pos, setPos] = useState(null);
  const boxRef = useRef(null);
  const ctrlRef = useRef(null);

  useEffect(() => {
    const layer = {};
    const ctrl = createTipController({
      getRegistry: () => registry,
      isEnabled: isHelpTipsEnabled,
      isSuppressed: () => getTourState().running,
      isActive: () => isActiveTipLayer(layer),
      onChange: (next) => {
        setPos(null);
        setTip(next);
      },
    });
    layer.hide = ctrl.hide;
    layer.toggle = ctrl.toggle;
    ctrlRef.current = ctrl;
    const unregister = registerTipLayer(layer);
    const uninstall = ctrl.install();
    const unsubscribe = subscribeHelpTips((on) => {
      if (!on) ctrl.hide();
    });
    return () => {
      unsubscribe();
      uninstall();
      unregister();
      ctrlRef.current = null;
    };
  }, [registry]);

  const place = useCallback(() => {
    const box = boxRef.current;
    if (!box || !tip) return;
    const next = placeTip({
      rect: tip.el.getBoundingClientRect(),
      width: box.offsetWidth,
      height: box.offsetHeight,
      vw: window.innerWidth,
      vh: window.innerHeight,
      side: tip.content.side || 'top',
    });
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, [tip]);

  useLayoutEffect(() => {
    place();
  }, [place]);

  // Follow the target while it shows; hide when it leaves the page.
  useEffect(() => {
    if (!tip) return undefined;
    const timer = setInterval(() => {
      if (ctrlRef.current && ctrlRef.current.check()) place();
    }, WATCH_MS);
    return () => clearInterval(timer);
  }, [tip, place]);

  if (!tip) return null;
  const { content } = tip;
  const rich = isHelpTip(content);
  const keys = keysOf(content);
  const along = pos && (pos.side === 'top' || pos.side === 'bottom');

  return ReactDOM.createPortal(
    <Box
      ref={boxRef}
      id={TIP_ID}
      role="tooltip"
      data-side={pos ? pos.side : undefined}
      $tone={tone}
      $rich={rich}
      $short={isShortLabel(content)}
      style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden' }}
    >
      {rich ? <Title>{content.t}</Title> : null}
      {content.b ? <Body $rich={rich}>{content.b}</Body> : null}
      {keys.length ? (
        <Keys $rich={rich}>
          {keys.map((key) => (
            <Kbd key={key}>{key}</Kbd>
          ))}
        </Keys>
      ) : null}
      {pos ? (
        <Arrow
          $side={pos.side}
          style={along ? { left: pos.arrow } : { top: pos.arrow }}
        />
      ) : null}
    </Box>,
    document.body
  );
};

export default TooltipLayer;
